import { services } from '@config/services';
import { catalogApi } from './base';
import type { CatalogVariantsResponse } from './types';

/**
 * Catalog-variant (products-in-a-catalog) read endpoints. The legacy app
 * uses two distinct URLs: `/variants/list` for plain paging and `/variants`
 * (with `searchAll` + sort) when a search string is present. Both responses
 * carry `.catalog`, `.variants`, and `.totalVariants`.
 */
export const variantsApi = catalogApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /catalog/v1/merchantCatalog/variants/list?id=&page=&pageSize= */
    getCatalogVariants: builder.query<
      CatalogVariantsResponse,
      { catalogId: string; page?: number; pageSize?: number }
    >({
      query: ({ catalogId, page = 1, pageSize = 25 }) => ({
        url: `${services.catalog}/v1/merchantCatalog/variants/list?id=${encodeURIComponent(catalogId)}&page=${page}&pageSize=${pageSize}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { catalogId, page = 1 }) => [
        { type: 'CatalogVariant', id: `LIST:${catalogId}` },
        { type: 'CatalogVariant', id: `${catalogId}:p${page}` },
      ],
    }),

    /** GET /catalog/v1/merchantCatalog/variants?id=&searchAll=&page=&pageSize=&sortBy=&sortAsc= */
    getCatalogVariantsSearch: builder.query<
      CatalogVariantsResponse,
      {
        catalogId: string;
        searchAll: string;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      }
    >({
      query: ({ catalogId, searchAll, page = 1, pageSize = 25, sortBy, sortDir }) => {
        const params = new URLSearchParams();
        params.set('id', catalogId);
        if (searchAll) params.set('searchAll', searchAll);
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        if (sortBy) params.set('sortBy', sortBy);
        if (sortDir) params.set('sortAsc', String(sortDir === 'asc'));
        return {
          url: `${services.catalog}/v1/merchantCatalog/variants?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (_r, _e, { catalogId, page = 1 }) => [
        { type: 'CatalogVariant', id: `LIST:${catalogId}` },
        { type: 'CatalogVariant', id: `${catalogId}:p${page}` },
      ],
    }),
  }),
});

export const {
  useGetCatalogVariantsQuery,
  useLazyGetCatalogVariantsQuery,
  useGetCatalogVariantsSearchQuery,
  useLazyGetCatalogVariantsSearchQuery,
} = variantsApi;
